import PortalSidebar from "../PortalSidebar/PortalSidebar";
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { format } from "date-fns";
import styles from "../../gmanewstyle.module.css";
import logoimage from "../assets/Group -2.png";

/* ────────── Pagination Helper ────────── */
const Pagination = ({ page, perPage, total, onChange }) => {
  const first = (page - 1) * perPage + 1;
  const last = Math.min(first + perPage - 1, total);
  const pages = Math.ceil(total / perPage);
  return (
    <div className={styles.pagination}>
      <button
        className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
        onClick={() => onChange(page - 1)}
        disabled={page === 1}
      >
        Previous
      </button>
      <span className={styles.pageMeta}>
        {first}-{last} of {total}
      </span>
      <button
        className={`${styles.pageBtn} ${styles.pageBtnPrimary}`}
        onClick={() => onChange(page + 1)}
        disabled={page === pages}
      >
        Next
      </button>
    </div>
  );
};

/* ────────── Trustee Account View ────────── */
export default function TrusteeAccViewPage() {
  const { accountId, buildingId } = useParams();
  const navigate = useNavigate();
  const buildingname = localStorage.getItem("buildingName");
  const [accountInfo, setAccountInfo] = useState(null);
  const [payments, setPayments] = useState([]);
  const [levyAccounts, setLevyAccounts] = useState([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payPage, setPayPage] = useState(1);
  const [levyPage, setLevyPage] = useState(1);
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  const perPage = 10;

  const fmtDate = (d) => {
    try {
      return format(new Date(d), "dd MMM yyyy");
    } catch {
      return d || "—";
    }
  };

  const fmtAmt = (v) =>
    isNaN(parseFloat(v))
      ? v ?? "—"
      : "R " +
        parseFloat(v)
          .toFixed(2)
          .replace(/\B(?=(\d{3})+(?!\d))/g, " ");

  const [userAccess, setUserAccess] = useState([]);
  const [loadingAccess, setLoadingAccess] = useState(true);

  const [displayName, setDisplayName] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Default sidebar state based on device width
  useEffect(() => {
  const handleResize = () => {
    const desktop = window.innerWidth >= 1024;
    setIsDesktop(desktop);

    // Only force closed when moving to smaller screens.
    // On desktop, keep whatever state the user last chose.
    if (!desktop) {
      setSidebarOpen(false);
    }
  };

  handleResize();
  window.addEventListener("resize", handleResize);

  return () => window.removeEventListener("resize", handleResize);
}, []);
  useEffect(() => {
  function handleClick(e) {
    // On desktop/laptop, only the burger should control the sidebar
    if (isDesktop) return;

    const sidebar = document.getElementsByClassName(styles.sidebar)[0];
    const hamburger = document.getElementsByClassName(styles.menuBtn)[0];

    if (!sidebar || !hamburger) return;

    const clickedInsideSidebar = sidebar.contains(e.target);
    const clickedHamburger = hamburger.contains(e.target);

    if (sidebarOpen && !clickedInsideSidebar && !clickedHamburger) {
      setSidebarOpen(false);
    }
  }

  document.addEventListener("click", handleClick);
  return () => document.removeEventListener("click", handleClick);
}, [sidebarOpen, isDesktop]);

  // pull user access for top-right dropdown
  useEffect(() => {
    const fetchUserAccess = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        navigate("/");
        return;
      }

      try {
        const decoded = JSON.parse(atob(token.split(".")[1]));
        const { email, contact_id, firstname, lastname, name } = decoded || {};

        // Set display name safely
        const fullName =
          firstname && lastname
            ? `${firstname} ${lastname}`
            : name
            ? name
            : email || "User";

        setDisplayName(fullName);

        if (!email) {
          setLoadingAccess(false);
          return;
        }

        const resp = await fetch(
          "https://portal.gmacc.co.za/api/contact/determine-access/",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ email, contact_id }),
          }
        );

        const data = await resp.json();
        if (resp.ok) {
          setUserAccess(data.access || []);
        }
      } catch (err) {
        console.error("Failed to fetch access:", err);
      } finally {
        setLoadingAccess(false);
      }
    };

    fetchUserAccess();
  }, [navigate]);


  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return navigate("/");

    (async () => {
      setLoading(true);
      try {
        const headers = { Authorization: `Bearer ${token}` };

        const [accRes, payRes, levyRes] = await Promise.all([
          fetch(`https://portal.gmacc.co.za/api/accounts/${accountId}/`, {
            headers,
          }),
          fetch(
            `https://portal.gmacc.co.za/api/accounts/${accountId}/payments/${buildingId}/`,
            { headers }
          ),
          fetch(
            `https://portal.gmacc.co.za/api/accounts/${accountId}/levy-accounts/${buildingId}/`,
            { headers }
          ),
        ]);

        if (!accRes.ok || !payRes.ok || !levyRes.ok) {
          throw new Error("Failed to load trustee account data");
        }

        const accJson = await accRes.json();
        const payJson = await payRes.json();
        const levyJson = await levyRes.json();

        setAccountInfo(accJson);
        setPayments(Array.isArray(payJson) ? payJson : payJson.value || []);
        setLevyAccounts(
          Array.isArray(levyJson) ? levyJson : levyJson.value || []
        );
      } catch (e) {
        console.error(e);
        setError("Could not load trustee account details.");
      } finally {
        setLoading(false);
      }
    })();
  }, [accountId, buildingId, navigate]);

  if (loading)
    return (
      <div className={styles.page}>
        <div className={styles.shell}>
          <main className={styles.contentArea}>
            <div className={styles.panel}>
              <div className={styles.loadingContainer}>
                <div className={styles.loadingImage}></div>
                <p className={styles.loadingText}>Loading...</p>
              </div>
            </div>
          </main>
        </div>
      </div>
    );
  if (error) return <p className={styles.errorText}>{error}</p>;
  if (!accountInfo) return null;

  const summaryFields = [
    ["Name", "name"],
    ["Email", "emailaddress1"],
    ["Passport/Reg/ID", "cj_registrationidpassport"],
  ];

  document.title = `${buildingname} - Account: ${accountInfo.name}`;
  return (
    <div className={styles.page}>
      {/* NAVBAR */}
      <header className={styles.topbar}>
        <div className={styles.topbarLeft}>
          <img src={logoimage} alt="GMA Logo" className={styles.topLogo} />

          {/* HAMBURGER: opens/closes sidebar */}
          <button
            type="button"
            className={styles.menuBtn}
            onClick={() => setSidebarOpen((v) => !v)}
          >
            ☰
          </button>

          <h1 className={styles.topTitle}>
            {buildingname} - Account: {accountInfo.name}
          </h1>
        </div>

        {/* USER CHIP: opens dropdown */}
        <div
          className={styles.userChip}
          onClick={() => {
            navigate("/user-profile");
          }}
          style={{ cursor: "pointer" }}
        >
          <span className={styles.statusDot}></span>
          <span className={styles.userName}>{displayName} ▾</span>
        </div>
      </header>

      <div className={styles.shell}>
        <PortalSidebar
  activePortal="trustee"
  activeTab="buildings"
  userAccess={userAccess}
  loadingAccess={loadingAccess}
  sidebarOpen={sidebarOpen}
  ticketRole="trustee"
/>
        <main className={styles.contentArea}>
          <div className={styles.panel}>
            {/* ACCOUNT SUMMARY */}
            <div className={styles.paymentHeaderCard}>
              <div className={styles.card}>
                {/* BACK BUTTON */}
                <button
                  className={styles.paymentBackBtn}
                  onClick={() => navigate(-1)}
                >
                  Back
                </button>
                {/* TITLE */}
                <h1 className={styles.paymentHeaderTitle}>Account Details</h1>

                {/* DIVIDER */}
                <div className={styles.paymentHeaderDivider} />

                {/* LABELS — AUTO GRID */}
                <div
                  className={styles.paymentHeaderGridAuto}
                  style={{ "--col-count": summaryFields.length }}
                >
                  {summaryFields.map(([label]) => (
                    <div key={label} className={styles.paymentHeaderLabelItem}>
                      {label}:
                    </div>
                  ))}
                </div>

                {/* VALUES — WHITE PILL */}
                <div className={styles.paymentHeaderValuePill}>
                  <div
                    className={styles.paymentHeaderGridAuto}
                    style={{ "--col-count": summaryFields.length }}
                  >
                    {summaryFields.map(([_, key]) => (
                      <div key={key} className={styles.paymentHeaderValueItem}>
                        {accountInfo[key] ?? "—"}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* PAYMENTS & LEVY ACCOUNTS (gap via grid‐container) */}
            <div className={styles.paymentCardGrid}>
              {/* PAYMENTS CARD */}
              <div className={styles.paymentCard}>
                <h2>Payments</h2>
                {payments.length === 0 ? (
                  <p className={styles.infoText}>No payments found.</p>
                ) : (
                  <>
                    <div className={styles.responsiveTableWrapper}>
                      <table className={styles.dataTable}>
                        <thead>
                          <tr>
                            <th>Payment Number</th>
                            <th>Building</th>
                            <th>Status</th>
                            <th>Amount</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments
                            .slice((payPage - 1) * perPage, payPage * perPage)
                            .map((p) => (
                              <tr key={p.cr446_paymentpk}>
                                <td
                                  onClick={() =>
                                    navigate(
                                      `/payment/${p.cr446_paymentid}/${buildingId}`
                                    )
                                  }
                                  style={{
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                  }}
                                >
                                  {p.cr446_paymentpk}
                                </td>
                                <td>
                                  {
                                    p[
                                      "_cr446_specifybuilding_value@OData.Community.Display.V1.FormattedValue"
                                    ]
                                  }
                                </td>
                                <td>
                                  {
                                    p[
                                      "cj_progress@OData.Community.Display.V1.FormattedValue"
                                    ]
                                  }
                                </td>
                                <td>{fmtAmt(p.cr446_amount)}</td>
                                <td>{fmtDate(p.createdon)}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    <Pagination
                      page={payPage}
                      perPage={perPage}
                      total={payments.length}
                      onChange={setPayPage}
                    />
                  </>
                )}
              </div>

              {/* LEVY ACCOUNTS CARD */}
              <div className={styles.paymentCard}>
                <h2>Levy Accounts</h2>
                {levyAccounts.length === 0 ? (
                  <p className={styles.infoText}>No levy accounts found.</p>
                ) : (
                  <>
                    <div className={styles.responsiveTableWrapper}>
                      <table className={styles.dataTable}>
                        <thead>
                          <tr>
                            <th>Account Number</th>
                            <th>Total</th>
                            <th>Current</th>
                            <th>30 Days</th>
                            <th>60 Days</th>
                            <th>90 Days</th>
                            <th>120 Days</th>
                            <th>150 Days</th>
                            <th>180 Days</th>
                          </tr>
                        </thead>
                        <tbody>
                          {levyAccounts
                            .slice((levyPage - 1) * perPage, levyPage * perPage)
                            .map((a) => (
                              <tr key={a.cj_sageaccountnumber}>
                                <td
                                  onClick={() =>
                                    navigate(
                                      `/levyaccount/${a.cj_gmaaccountnumberid}/${buildingId}`
                                    )
                                  }
                                  style={{
                                    cursor: "pointer",
                                    textDecoration: "underline",
                                  }}
                                >
                                  {a.cj_sageaccountnumber}
                                </td>
                                <td>{fmtAmt(a.cj_agetotal)}</td>
                                <td>{fmtAmt(a.cj_agecurrent)}</td>
                                <td>{fmtAmt(a.cj_age30days)}</td>
                                <td>{fmtAmt(a.cj_age60days)}</td>
                                <td>{fmtAmt(a.cj_age90days)}</td>
                                <td>{fmtAmt(a.cj_age120days)}</td>
                                <td>{fmtAmt(a.cj_age150days)}</td>
                                <td>{fmtAmt(a.cj_age180days)}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    <Pagination
                      page={levyPage}
                      perPage={perPage}
                      total={levyAccounts.length}
                      onChange={setLevyPage}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}
